import { type FC, useEffect, useState } from 'react';

import type { CancellationReason, CapabilitiesOutcome } from '../../../lib/cancellation-api';
import { reasonLabel } from './reasons';

/** Data contract for opening the `pu-cancellation` panel. */
export interface IPuCancellationData {
	electionId: number;
	pollingUnitCode: string;
	pollingUnitName?: string;
	/** `add-evidence` opens straight on the evidence step for an existing report. */
	mode?: 'report' | 'add-evidence';
	/** Photos taken from the home row's camera before the panel opened. */
	initialPhotos?: File[];
}

/**
 * Can evidence be added to a filed report? Only a server that has SAID no (v1 ledger, or v2 with
 * `evidence: false`) hides it; unknown (offline, not asked yet) keeps it, so files can queue.
 */
export const evidenceAllowed = (caps: CapabilitiesOutcome | null | undefined): boolean =>
	!caps || caps.mode === 'unknown' || (caps.mode === 'v2' && caps.caps.evidence);

export const clockTime = (iso?: string | number | null): string => {
	if (iso == null || iso === '') return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

// ── Discreet mode ──
// Per device. Drains the stamp of colour so a glance over the agent's shoulder reads nothing.
const DISCREET_KEY = 'ielect-pu-cancel-discreet';
const DISCREET_EVENT = 'pu-cancel:discreet';

const readDiscreet = () => {
	try {
		return localStorage.getItem(DISCREET_KEY) === 'on';
	} catch {
		return false;
	}
};

export const useDiscreetMode = (): [boolean, (on: boolean) => void] => {
	const [on, setOn] = useState(readDiscreet);
	useEffect(() => {
		const sync = () => setOn(readDiscreet());
		window.addEventListener(DISCREET_EVENT, sync);
		return () => window.removeEventListener(DISCREET_EVENT, sync);
	}, []);
	const set = (next: boolean) => {
		try {
			localStorage.setItem(DISCREET_KEY, next ? 'on' : 'off');
		} catch {
			// Storage blocked — this session only.
		}
		setOn(next);
		window.dispatchEvent(new Event(DISCREET_EVENT));
	};
	return [on, set];
};

/**
 * The signature: the stamp a presiding officer writes across the EC8A sheet, carried onto the
 * agent's own assignment card so anyone the phone is handed to reads the unit's state at once.
 */
export const CancelledStamp: FC<{
	reason?: CancellationReason;
	cancelledAt?: string;
	discreet?: boolean;
	localOnly?: boolean;
}> = ({ reason, cancelledAt, discreet, localOnly }) => (
	<div
		className={`pc-stamp${discreet ? ' pc-stamp--discreet' : ''}`}
		role='img'
		aria-label={`Cancelled by INEC. ${reasonLabel(reason)}${cancelledAt ? ` at ${clockTime(cancelledAt)}` : ''}.`}
	>
		<span className='pc-stamp__head'>Cancelled by INEC</span>
		<span className='pc-stamp__sub'>
			{reasonLabel(reason)}
			{cancelledAt ? ` · ${clockTime(cancelledAt)}` : ''}
			{localOnly ? ' · not sent yet' : ''}
		</span>
	</div>
);

export const EyeOffGlyph = () => (
	<svg
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M3 3l18 18M10.6 5.1A9.8 9.8 0 0 1 12 5c5 0 9 5 9 7a10.6 10.6 0 0 1-2.6 3.6M6.1 6.1C3.9 7.6 3 10.6 3 12c0 2 4 7 9 7 1.6 0 3-.4 4.3-1.1' />
		<path d='M9.9 9.9a3 3 0 0 0 4.2 4.2' />
	</svg>
);

export const CameraGlyph = ({ size = 20 }: { size?: number }) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<path d='M4 8h3l2-3h6l2 3h3v11H4z' />
		<circle
			cx='12'
			cy='13'
			r='3.5'
		/>
	</svg>
);
